import { useAtom } from 'ximple';
import { paymentPlanAtom } from 'src/state/payment-plan/paymentPlanAtom';
import { Box, Button, CircularProgress, Collapse, Grid, useTheme } from '@mui/material';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { useLocale } from '@repo/i18n';
import InfoBox from 'src/components/common/info-box/InfoBox';
import { ButtonDisplay } from 'src/widgets/activities/checkout/cart/views/ButtonDisplay';
import PaymentOptionsPanel from '../timeslots-payment-options-panel/PaymentOptionsPanel';
import { useCartCheckoutContext } from 'src/widgets/activities/checkout/cart/CartCheckoutContext';
import CheckoutSummary, {
    useSummaryFeatures,
} from 'src/widgets/activities/checkout/CheckoutSummary';
import useCartItemsForSummary from 'src/hooks/domain/useCartItemsForSummary';

type IntentViewProps = {
    handleGoPayClicked: () => void;
    isPaymentRequired: boolean;
};

export default function IntentView(props: IntentViewProps) {
    const { handleGoPayClicked, isPaymentRequired } = props;
    const { t } = useLocale();

    const [{ isLoadingPrices: isLoadingIntents }] = useAtom(paymentPlanAtom);
    const { activeTab, largeScreen, isMakingReservation } = useCartCheckoutContext();

    const cartItemsForSummary = useCartItemsForSummary();

    const showShare = true;
    const enableCartClearing = true;
    const enableItemEditing = true;

    const summaryFeatures = useSummaryFeatures(
        showShare,
        enableCartClearing,
        enableItemEditing,
        activeTab,
        cartItemsForSummary.isWaitingListMode,
    );

    const noPaymentCallout = (
        <Collapse in={!isPaymentRequired && !isLoadingIntents} unmountOnExit>
            <InfoBox text={t.no_payment_required} infoType="information" compact />
        </Collapse>
    );

    return (
        <>
            <Grid
                container
                justifyContent={largeScreen ? 'space-between' : 'center'}
                alignItems={largeScreen ? 'flex-start' : 'center'}
                direction={largeScreen ? 'row' : 'column-reverse'}
            >
                {!largeScreen && !isLoadingIntents && (
                    <>
                        <GoPayButton
                            isMakingReservation={isMakingReservation}
                            handleGoPayClicked={handleGoPayClicked}
                            isPaymentRequired={isPaymentRequired}
                        ></GoPayButton>
                        {noPaymentCallout}
                    </>
                )}

                <Box pt={2}>
                    {!isLoadingIntents && <PaymentOptionsPanel />}
                    {isLoadingIntents && <CircularProgress color="primary" />}
                </Box>

                <CheckoutSummary
                    cartItems={cartItemsForSummary.itemsToShow}
                    hideGoPay={!largeScreen}
                    isMakingReservation={isMakingReservation}
                    goPayClicked={handleGoPayClicked}
                    summaryFeatures={summaryFeatures}
                    nextButtonText={isPaymentRequired ? undefined : t.confirm_booking}
                    beforeGoPay={largeScreen ? noPaymentCallout : undefined}
                />
            </Grid>
        </>
    );
}

function GoPayButton(props: {
    isMakingReservation: boolean;
    handleGoPayClicked: () => void;
    isPaymentRequired: boolean;
}) {
    const theme = useTheme();
    const { t } = useLocale();
    const customizations = useCustomizations();

    return (
        <Box
            maxWidth="440px"
            width="100%"
            sx={{
                [theme.breakpoints.down('md')]: {
                    marginBottom: theme.spacing(3),
                },
            }}
        >
            <Button
                sx={{ float: 'right', marginTop: theme.spacing(2) }}
                color="primary"
                variant={customizations.primaryButtonStyle}
                disabled={props.isMakingReservation}
                onClick={props.handleGoPayClicked}
            >
                <ButtonDisplay
                    isMakingReservation={props.isMakingReservation}
                    buttonText={props.isPaymentRequired ? undefined : t.confirm_booking}
                />
            </Button>
        </Box>
    );
}
